/**
 * 训练趋势聚合（阶段 6，纯函数、可单测）。
 *
 * 铁律（ADR-006 / 修正 2 / §12.4）：
 *   - 重量/次数/e1RM 只在**同一动作 + 同一重量口径**内比较（weightUnitScope 守卫）；
 *   - e1RM 用 Epley，明确标注「估算」，不鼓励真实 1RM 测试；
 *   - 数据不足时输出空态（不画误导性趋势线）。
 *
 * 输入全部由调用方提供（时间序列取自 IndexedDB），规则层不读时钟。
 */

import type { RirValue, WeightUnitScope, WorkoutSession, WorkoutSet } from "@jilian/shared";
import { calculateEpley1RM } from "./weights";

/** 单次训练中某动作的一个表现点（取该次训练该动作最后一组完成数据）。 */
export interface ExercisePoint {
  /** 训练开始本地日（ADR-010 归属日） */
  date: string;
  weightKg: number;
  reps: number;
  rir: RirValue;
  e1rm: number;
  weightUnitScope: WeightUnitScope;
  sessionId: string;
}

export interface ExerciseSeriesInput {
  /** 该动作的组记录（来自一次或多次训练） */
  sets: WorkoutSet[];
  /** 组 → 归属训练日与归属会话（同长度；依顺序与 sets 对应） */
  dayBySet: string[];
  sessionIdBySet: string[];
  /** 该动作的重量口径（同口径铁律） */
  weightUnitScope: WeightUnitScope;
}

/**
 * 构建单动作表现序列：按训练日排序，同一训练日保留**传入顺序的最后一条**
 * （调用方须按 completedAt 升序传入；即同日的“最后一组完成记录”）。
 * 口径不符的组被剔除（绝不跨口径混合）。
 */
export function buildExerciseSeries(input: ExerciseSeriesInput): ExercisePoint[] {
  const scope = input.weightUnitScope;
  const byDay = new Map<string, ExercisePoint>();
  for (let i = 0; i < input.sets.length; i++) {
    const s = input.sets[i]!;
    if (s.status !== "completed" || s.actualWeightKg == null || s.reps == null || s.rir == null) continue;
    if (s.weightUnitScope !== scope) continue; // 口径守卫：不同口径不混合
    const date = input.dayBySet[i] ?? "";
    byDay.set(date, {
      date,
      weightKg: s.actualWeightKg,
      reps: s.reps,
      rir: s.rir,
      e1rm: calculateEpley1RM(s.actualWeightKg, s.reps),
      weightUnitScope: scope,
      sessionId: input.sessionIdBySet[i] ?? "",
    });
  }
  return [...byDay.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** 每周正式训练次数（近 N 周；按归属日 localDateAtStart；休息日不计）。 */
export function weeklyTrainingCounts(
  sessions: ReadonlyArray<Pick<WorkoutSession, "localDateAtStart" | "status">>,
  weeks: number,
  today: string,
): Array<{ weekLabel: string; count: number }> {
  const out: Array<{ weekLabel: string; count: number }> = [];
  const now = new Date(today + "T00:00:00");
  for (let w = weeks - 1; w >= 0; w--) {
    const start = new Date(now.getTime() - w * 7 * 86400000);
    const end = new Date(start.getTime() + 7 * 86400000);
    const label = `${start.getMonth() + 1}/${start.getDate()}`;
    const count = sessions.filter((s) => {
      const d = new Date(s.localDateAtStart + "T00:00:00");
      return d >= start && d < end && (s.status === "completed" || s.status === "ended_early");
    }).length;
    out.push({ weekLabel: label, count });
  }
  return out;
}

/** 数据是否足够画趋势（点数下限）。 */
export function hasEnoughTrendData(points: readonly unknown[], minimum: number): boolean {
  return points.length >= minimum;
}